import {
  validatePAN,
  validateGST,
  validateMobile,
  validateEmail,
  validateIFSC,
  validateAccountNumber,
} from "../../../../utils/validationUtils";
import {
  validateAgreementStartDate,
  validateAgreementEndDate,
} from "../../../../utils/dateUtils";
import DropDown from "../../../../components/DropDown";
import { fetchPincodeDetailsAction } from "../../../../actions/pincodeAction";
import { fetchSpocEmailsAction } from "../../../../actions/fetchSpocEmailsAction";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PARENT_CHILD_MAP } from "../../../../constants/companyDistributorData";
import RequireKycModal from "../RequireKycModal";
import {
  clientFormFields,
  LOB_OPTIONS_CLIENT,
  LOB_OPTIONS_SERVICE_PARTNER,
  LOB_OPTIONS_ALL,
  AA_DEPT_MAP,
  DEPT_OPTIONS_BASE,
} from "./ClientFields";

export { clientFormFields, PARENT_CHILD_MAP };

export const initialClientState = {
  parentCode: "",
  childCode: "",
  entityCategory: "",
  EntityName: "",
  entityLOBType: "",
  selectedlobtypenationality: "",
  selectedrequirekyc: "",
  aaDepartmentFunction: "",
  contactPersonEmail: "",
  spocEmailAddress: "",
  entityName: "",
  address: "",
  pinCode: "",
  city: "",
  State: "",
  natureOfBusiness: "",
  entityType: "",
  panNumber: "",
  CompanypanNumber: "",
  isGSTRegistered: "",
  gstNumberPrimary: "",
  gstAddress: "",
  gstNumbersOther: "",
  isMSMERegistered: "",
  contactPersonName: "",
  contactPersonDesignation: "",
  contactPersonMobile: "",
  hasBankDetails: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountType: "",
  branchName: "",
  agreementStartDate: "",
  agreementEndDate: "",
  sendEmailReceipt: false,
};

export const useClientForm = (
  formData,
  errors,
  onChange,
  onChangeMultiple,
  onBlur,
  onSubmit,
  setErrors,
  isSubmitting,
  parentOptions = [],
  isSpoc = false,
) => {
  const [showKycModal, setShowKycModal] = useState(false);
  const [pendingKycValue, setPendingKycValue] = useState(null);
  const [spocEmailOptions, setSpocEmailOptions] = useState([]);
  const [isLoadingSpocEmails, setIsLoadingSpocEmails] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    clientFormFields.forEach((field) => {
      if (field.type === "section") return;
      if (
        field.conditionalOn &&
        formData[field.conditionalOn] !== field.conditionalValue
      )
        return;
      const value = formData[field.name];
      if (
        field.required &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        newErrors[field.name] =
          field.name === "spocEmailAddress" &&
          formData.entityLOBType &&
          formData.aaDepartmentFunction &&
          spocEmailOptions.length === 0
            ? "Please create a SPOC in this LOB"
            : `${field.label} is required`;
      } else if (field.validation && value && !Array.isArray(value)) {
        const result = field.validation(value);
        if (result !== true) newErrors[field.name] = result;
      }
    });
    return newErrors;
  };

  useEffect(() => {
    const pinCode = formData.pinCode || "";
    if (pinCode.length !== 6) {
      if (pinCode.length > 0) onChangeMultiple({ city: "", State: "" });
      return;
    }
    fetchPincodeDetailsAction(pinCode).then((result) => {
      if (result.success) {
        onChangeMultiple({ city: result.city, State: result.state });
        setErrors((prev) => {
          const e = { ...prev };
          delete e.pinCode;
          return e;
        });
      } else {
        onChangeMultiple({ city: "", State: "" });
        setErrors((prev) => ({ ...prev, pinCode: result.error }));
      }
    });
  }, [formData.pinCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch SPOC emails when entityLOBType and aaDepartmentFunction are selected ──
  useEffect(() => {
    let isCancelled = false;
    const lob = formData.entityLOBType;
    const dept = formData.aaDepartmentFunction;

    if (!lob || !dept) {
      setSpocEmailOptions([]);
      return;
    }

    setIsLoadingSpocEmails(true);

    fetchSpocEmailsAction({
      entityLOBType: lob,
      aaDepartmentFunction: dept,
    })
      .then((result) => {
        if (isCancelled) return;
        setIsLoadingSpocEmails(false);
        const emails = result?.emails || [];

        if (emails.length > 0) {
          setSpocEmailOptions(emails);
          const currentSpoc = formData.spocEmailAddress;
          const selectedEmail =
            currentSpoc && emails.includes(currentSpoc)
              ? currentSpoc
              : emails[0];

          onChange("spocEmailAddress", selectedEmail);
          setErrors((prev) => {
            const e = { ...prev };
            delete e.spocEmailAddress;
            return e;
          });
        } else {
          setSpocEmailOptions([]);
          if (!isSpoc) {
            onChange("spocEmailAddress", "");
          }
          toast.warn("Please create a SPOC in this LOB", {
            position: "top-right",
            autoClose: 5000,
            toastId: "no-spoc-found",
          });
          setErrors((prev) => ({
            ...prev,
            spocEmailAddress: "Please create a SPOC in this LOB",
          }));
        }
      })
      .catch((err) => {
        if (isCancelled) return;
        setIsLoadingSpocEmails(false);
        setSpocEmailOptions([]);
        if (!isSpoc) {
          onChange("spocEmailAddress", "");
        }
        toast.warn("Please create a SPOC in this LOB", {
          position: "top-right",
          autoClose: 5000,
          toastId: "no-spoc-found",
        });
        setErrors((prev) => ({
          ...prev,
          spocEmailAddress: "Please create a SPOC in this LOB",
        }));
      });

    return () => {
      isCancelled = true;
    };
  }, [formData.entityLOBType, formData.aaDepartmentFunction]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // Handle Require KYC selection - show modal if "No" is selected
    if (name === "selectedrequirekyc" && newValue === "No") {
      setPendingKycValue(newValue);
      setShowKycModal(true);
      return; // Don't update the value yet, wait for confirmation
    }

    const uppercaseFields = [
      "panNumber",
      "CompanypanNumber",
      "gstNumberPrimary",
      "ifscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    if (
      (name === "contactPersonMobile" || name === "accountNumber") &&
      typeof newValue === "string"
    )
      newValue = newValue.replace(/\D/g, "");

    if (name === "pinCode") {
      onChange(name, newValue.replace(/\D/g, "").slice(0, 6));
      return;
    }

    if (name === "parentCode") onChange("childCode", "");
    if (name === "entityCategory") onChange("entityLOBType", "");
    if (name === "entityLOBType") onChange("aaDepartmentFunction", "");

    if (
      !isSpoc &&
      (name === "entityCategory" ||
        name === "entityLOBType" ||
        name === "aaDepartmentFunction")
    ) {
      onChange("spocEmailAddress", "");
      setSpocEmailOptions([]);
      setErrors((prev) => {
        const e = { ...prev };
        delete e.spocEmailAddress;
        return e;
      });
    }

    if (name === "hasBankDetails" && newValue === "no") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "bankName",
          "accountNumber",
          "ifscCode",
          "accountType",
          "branchName",
        ].forEach((f) => delete e[f]);
        return e;
      });
    }

    onChange(name, newValue);

    if (type !== "checkbox") {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        if (name === "agreementStartDate" || name === "agreementEndDate") {
          const updatedStart =
            name === "agreementStartDate"
              ? newValue
              : formData.agreementStartDate;
          const updatedEnd =
            name === "agreementEndDate" ? newValue : formData.agreementEndDate;
          const startErr = validateAgreementStartDate(updatedStart, updatedEnd);
          const endErr = validateAgreementEndDate(updatedEnd, updatedStart);
          if (startErr) newErrors.agreementStartDate = startErr;
          else delete newErrors.agreementStartDate;
          if (endErr) newErrors.agreementEndDate = endErr;
          else delete newErrors.agreementEndDate;
        }
        return newErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value || !value.trim()) return;
    const blurValidations = {
      panNumber: validatePAN,
      CompanypanNumber: validatePAN,
      gstNumberPrimary: validateGST,
      contactPersonMobile: validateMobile,
      contactPersonEmail: validateEmail,
      spocEmailAddress: validateEmail,
      ifscCode: validateIFSC,
      accountNumber: validateAccountNumber,
    };
    const validator = blurValidations[name];
    if (validator) {
      const result = validator(value);
      if (result !== true) setErrors((prev) => ({ ...prev, [name]: result }));
      else
        setErrors((prev) => {
          const e = { ...prev };
          delete e[name];
          return e;
        });
    }
    if (onBlur) onBlur(name, value);
  };

  const shouldShowField = (field) => {
    if (!field.conditionalOn) return true;
    return formData[field.conditionalOn] === field.conditionalValue;
  };

  const getFieldClassName = (field) => {
    const classes = ["form-group"];
    if (field.type === "file" || field.type === "textarea")
      classes.push("full-width");
    return classes.join(" ");
  };

  const renderField = (field) => {
    if (!shouldShowField(field)) return null;
    const value = formData[field.name] || "";
    const error = errors[field.name];
    const hasValue = value && value.toString().trim() !== "";
    const isValid = hasValue && !error && field.validation;

    if (field.type === "section") {
      return (
        <div key={field.name} className="form-section-header">
          <h3>{field.label}</h3>
        </div>
      );
    }

    if (field.type === "textarea") {
      return (
        <div key={field.name} className={getFieldClassName(field)}>
          <label htmlFor={field.name}>
            {field.label}
            {field.required && <span className="required">*</span>}
          </label>
          <textarea
            id={field.name}
            name={field.name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={field.placeholder}
            rows={field.rows || 3}
            className={error ? "error" : ""}
            disabled={isSubmitting}
          />
          {error && <span className="error-message">{error}</span>}
        </div>
      );
    }

    if (field.type === "select") {
      let selectOptions = field.options;

      if (field.name === "childCode") {
        selectOptions = formData.parentCode
          ? PARENT_CHILD_MAP[formData.parentCode] || [
              { value: "", label: "Select Distributor" },
            ]
          : [{ value: "", label: "Select company first" }];
      }
      if (field.name === "parentCode" && parentOptions.length > 1) {
        selectOptions = parentOptions;
      }
      if (field.name === "entityLOBType") {
        if (formData.entityCategory === "client")
          selectOptions = LOB_OPTIONS_CLIENT;
        else if (formData.entityCategory === "Service Partner")
          selectOptions = LOB_OPTIONS_SERVICE_PARTNER;
        else selectOptions = LOB_OPTIONS_ALL;
      }
      if (field.name === "aaDepartmentFunction") {
        const categoryMap = AA_DEPT_MAP[formData.entityCategory] || {};
        selectOptions =
          categoryMap[formData.entityLOBType] || DEPT_OPTIONS_BASE;
      }

      return (
        <div key={field.name} className={getFieldClassName(field)}>
          <label htmlFor={field.name}>
            {field.label}
            {field.required && <span className="required">*</span>}
          </label>
          <DropDown
            id={field.name}
            name={field.name}
            value={value}
            onChange={handleChange}
            options={selectOptions}
            error={error}
            required={field.required}
            disabled={
              isSubmitting ||
              (field.name === "childCode" && !formData.parentCode)
            }
            fullWidth
          />
        </div>
      );
    }

    if (field.type === "checkbox") {
      return (
        <div
          key={field.name}
          className="form-group form-group-checkbox full-width"
        >
          <label className="checkbox-label">
            <input
              type="checkbox"
              id={field.name}
              name={field.name}
              checked={value || false}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <span className="checkbox-text">
              {field.label}
              {field.required && <span className="required">*</span>}
            </span>
          </label>
          {field.helpText && (
            <small className="help-text">{field.helpText}</small>
          )}
          {error && <span className="error-message">{error}</span>}
        </div>
      );
    }

    if (field.type === "radio") {
      return (
        <div key={field.name} className="form-group full-width">
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <label style={{ marginRight: "10px" }}>
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            {["yes", "no"].map((v) => (
              <label
                key={v}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <input
                  type="radio"
                  name={field.name}
                  value={v}
                  checked={value === v}
                  onChange={handleChange}
                />
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </label>
            ))}
          </div>
          {error && <span className="error-message">{error}</span>}
        </div>
      );
    }

    const readonlyFields = ["city", "State"];
    const isReadonly =
      readonlyFields.includes(field.name) ||
      (isSpoc && field.name === "spocEmailAddress");

    if (field.name === "spocEmailAddress") {
      if (isLoadingSpocEmails) {
        return (
          <div key={field.name} className={getFieldClassName(field)}>
            <label htmlFor={field.name}>
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <input
              type="text"
              id={field.name}
              name={field.name}
              value=""
              placeholder="Fetching SPOC emails..."
              className="readonly-field"
              disabled
              readOnly
            />
          </div>
        );
      }

      if (spocEmailOptions.length > 0) {
        const spocSelectOptions = [
          ...(spocEmailOptions.length > 1
            ? [{ value: "", label: "Select SPOC email" }]
            : []),
          ...spocEmailOptions.map((em) => ({ value: em, label: em })),
        ];

        return (
          <div key={field.name} className={getFieldClassName(field)}>
            <label htmlFor={field.name}>
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            <DropDown
              id={field.name}
              name={field.name}
              value={value}
              onChange={handleChange}
              options={spocSelectOptions}
              error={error}
              required={field.required}
              disabled={
                isSubmitting || (isSpoc && spocEmailOptions.length <= 1)
              }
              fullWidth
            />
          </div>
        );
      }
    }

    return (
      <div key={field.name} className={getFieldClassName(field)}>
        <label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="required">*</span>}
        </label>
        <input
          type={field.type}
          id={field.name}
          name={field.name}
          value={value}
          onChange={isReadonly ? undefined : handleChange}
          onBlur={isReadonly ? undefined : handleBlur}
          placeholder={
            field.name === "spocEmailAddress"
              ? !formData.entityLOBType || !formData.aaDepartmentFunction
                ? "Select Entity LOB & Department to fetch SPOC"
                : "No SPOC found — please create a SPOC in this LOB"
              : isReadonly
                ? "Auto-filled from pincode"
                : field.placeholder
          }
          className={[
            error ? "error" : isValid ? "success" : "",
            isReadonly ? "readonly-field" : "",
          ]
            .join(" ")
            .trim()}
          disabled={isSubmitting}
          readOnly={isReadonly}
          maxLength={field.maxLength}
        />
        {error && <span className="error-message">{error}</span>}
      </div>
    );
  };

  const handleKycModalConfirm = () => {
    // User confirmed they want to select "No"
    onChange("selectedrequirekyc", pendingKycValue);
    setShowKycModal(false);
    setPendingKycValue(null);
  };

  const handleKycModalCancel = () => {
    // User cancelled, reset to previous value or empty
    setShowKycModal(false);
    setPendingKycValue(null);
    // Optionally reset the dropdown to previous value or empty
    // onChange("selectedrequirekyc", "");
  };

  const renderKycModal = () => {
    return (
      <RequireKycModal
        isOpen={showKycModal}
        onClose={handleKycModalCancel}
        onConfirm={handleKycModalConfirm}
        onCancel={handleKycModalCancel}
      />
    );
  };

  return { handleSubmit, renderField, handleBlur, renderKycModal };
};
